import React, { useEffect, useRef } from 'react';

const styles = require('./SidePanel.module.scss');

const FOCUSABLE_SELECTOR =
  'button:not([disabled]), input:not([disabled]), [tabindex="0"], a[href], select, textarea';

interface SidePanelProps {
  visible: boolean;
  onClose: () => void;
  labelledBy?: string;
  panelClassName?: string;
  children: React.ReactNode;
}

export default function SidePanel({
  visible,
  onClose,
  labelledBy,
  panelClassName,
  children
}: SidePanelProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!visible) return;

    triggerRef.current = document.activeElement as HTMLElement;
    document.body.style.overflow = 'hidden';

    const raf = requestAnimationFrame(() => {
      const first = panelRef.current?.querySelector<HTMLElement>(FOCUSABLE_SELECTOR);
      first?.focus();
    });

    const restoreTrigger = triggerRef.current;
    return () => {
      cancelAnimationFrame(raf);
      document.body.style.overflow = '';
      restoreTrigger?.focus();
    };
  }, [visible]);

  useEffect(() => {
    if (!visible) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
        return;
      }
      if (e.key !== 'Tab') return;

      const panel = panelRef.current;
      if (!panel) return;

      const focusables = panel.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);
      if (focusables.length === 0) return;

      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [visible, onClose]);

  if (!visible) return null;

  return (
    <div className={styles.overlay} role="dialog" aria-modal="true" aria-labelledby={labelledBy}>
      <div className={styles.backdrop} onClick={onClose} />
      <div ref={panelRef} className={panelClassName}>
        {children}
      </div>
    </div>
  );
}
